import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GraduationCap, FileSignature, NotebookPen, CheckSquare, Wand2, ArrowRight, BookOpen, Lightbulb, Sparkles, Pin } from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";

export default function HeadAcademicsDashboard() {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [docs, setDocs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [quickNote, setQuickNote] = useState({ title: "", body: "", color: "amber" });

  const load = async () => {
    const [n, d, t] = await Promise.all([
      api.get("/notes").catch(() => ({ data: [] })),
      api.get("/documents").catch(() => ({ data: [] })),
      api.get("/tasks").catch(() => ({ data: [] })),
    ]);
    setNotes(n.data); setDocs(d.data); setTasks(t.data);
  };
  useEffect(() => { load(); }, []);

  const addQuickNote = async (e) => {
    e.preventDefault();
    if (!quickNote.title && !quickNote.body) return;
    try {
      await api.post("/notes", quickNote);
      setQuickNote({ title: "", body: "", color: "amber" });
      toast.success("Note pinned to your desk");
      load();
    } catch (e2) { toast.error("Failed to save"); }
  };

  const togglePin = async (n) => {
    await api.patch(`/notes/${n.note_id}`, { pinned: !n.pinned });
    load();
  };

  const myDocs = docs.filter((d) => d.owner_id === user.user_id);
  const myTasksOpen = tasks.filter((t) => (t.assignee_ids || []).includes(user.user_id) && t.status !== "done");
  const colorMap = {
    amber: "bg-amber-50 border-amber-200",
    rose: "bg-rose-50 border-rose-200",
    teal: "bg-teal-50 border-teal-200",
    indigo: "bg-indigo-50 border-indigo-200",
    slate: "bg-slate-50 border-slate-200",
  };

  return (
    <Layout navItems={getNav(user.role)}>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto">
        <PageHeader
          title={`Strategy Desk · ${user.name.split(" ")[0]}`}
          subtitle="Plan research, draft proposals, share documents with Admin, and brief the Media team."
          icon={GraduationCap}
          accent="cyan"
        />

        {/* Quick stats */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <StatCard icon={FileSignature} label="My Documents" value={myDocs.length} color="cyan" to="/dashboard/documents" />
          <StatCard icon={NotebookPen} label="Pinned Notes" value={notes.filter((n) => n.pinned).length} color="amber" to="/dashboard/notes" />
          <StatCard icon={CheckSquare} label="Open Tasks" value={myTasksOpen.length} color="orange" to="/dashboard/tasks" />
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Quick actions panel */}
          <div className="lg:col-span-2 dashboard-card p-6 h-fit">
            <h3 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><Lightbulb className="w-4 h-4 text-cyan-600" /> Quick actions</h3>
            <div className="space-y-2">
              <ActionLink to="/dashboard/documents" Icon={FileSignature} title="Open Documents" subtitle="Link Google Docs you've created to the dashboard" />
              <ActionLink to="/dashboard/tasks" Icon={CheckSquare} title="Assign work to Head of M&C" subtitle="Brief the content team on what's coming up" />
              <ActionLink to="/dashboard/ai" Icon={Wand2} title="AI Content Creator" subtitle="Draft SEO-friendly text and quick documents" />
              <ActionLink to="/dashboard/meetings" Icon={BookOpen} title="Schedule a meeting" subtitle="Coordinate with Admin or Heads via Jitsi" />
            </div>
          </div>

          {/* Quick note composer */}
          <form onSubmit={addQuickNote} className="lg:col-span-3 dashboard-card p-6">
            <h3 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-cyan-600" /> Capture a thought</h3>
            <input className="input-pill mb-2" placeholder="Title (optional)" value={quickNote.title} onChange={(e) => setQuickNote({ ...quickNote, title: e.target.value })} data-testid="quick-note-title" />
            <textarea rows={3} className="input-pill mb-2" placeholder="What's on your mind?" value={quickNote.body} onChange={(e) => setQuickNote({ ...quickNote, body: e.target.value })} data-testid="quick-note-body" />
            <div className="flex items-center gap-2">
              {["amber", "rose", "teal", "indigo", "slate"].map((c) => (
                <button type="button" key={c} onClick={() => setQuickNote({ ...quickNote, color: c })} className={`w-7 h-7 rounded-full border-2 ${quickNote.color === c ? "border-slate-900" : "border-slate-200"} ${colorMap[c]}`} data-testid={`note-color-${c}`} />
              ))}
              <button type="submit" className="btn-primary ml-auto py-2 px-4 text-sm" data-testid="quick-note-save">Save note</button>
            </div>

            {/* Recent notes preview */}
            {notes.length > 0 && (
              <div className="mt-5 pt-5 border-t border-slate-100">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Recent</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {notes.slice(0, 4).map((n) => (
                    <motion.div key={n.note_id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                      className={`p-3 rounded-xl border ${colorMap[n.color] || colorMap.amber} text-sm`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-semibold text-slate-800 truncate">{n.title || "Untitled"}</div>
                        <button onClick={() => togglePin(n)} className={`p-1 rounded ${n.pinned ? "text-cyan-600" : "text-slate-400"}`}><Pin className="w-3.5 h-3.5" /></button>
                      </div>
                      <div className="text-xs text-slate-600 line-clamp-2 mt-1">{n.body}</div>
                    </motion.div>
                  ))}
                </div>
                <Link to="/dashboard/notes" className="text-xs text-cyan-600 hover:underline mt-3 inline-flex items-center gap-1">All notes <ArrowRight className="w-3 h-3" /></Link>
              </div>
            )}
          </form>
        </div>
      </div>
    </Layout>
  );
}

function StatCard({ icon: Icon, label, value, color, to }) {
  const c = { cyan: "from-cyan-500 to-teal-500", amber: "from-amber-400 to-orange-500", orange: "from-orange-500 to-pink-500" }[color] || "from-slate-400 to-slate-600";
  return (
    <Link to={to} className="dashboard-card p-5 flex items-center gap-4 hover:shadow-md transition-shadow group">
      <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${c} text-white flex items-center justify-center shadow-md group-hover:scale-105 transition-transform`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <div className="text-2xl font-display font-black">{value}</div>
        <div className="text-xs text-slate-500">{label}</div>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-orange-500" />
    </Link>
  );
}

function ActionLink({ to, Icon, title, subtitle }) {
  return (
    <Link to={to} className="flex items-start gap-3 p-3 rounded-xl hover:bg-orange-50 border border-transparent hover:border-orange-200 transition-all">
      <div className="w-9 h-9 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm">{title}</div>
        <div className="text-xs text-slate-500">{subtitle}</div>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-300 mt-2" />
    </Link>
  );
}
